import { useState, useRef, useEffect } from 'react';

export function useMenuVisibility({ editor }) {
  const [plusPos, setPlusPos] = useState({ top: 370, left: 150 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(0);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [menuSource, setMenuSource] = useState('editor');
  const plusRef = useRef();
  const menuRef = useRef();
  const inputRef = useRef();
  const editorRef = useRef();

  // Scroll handling to keep icons visible
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Drag mousemove/mouseup
  useEffect(() => {
    if (!dragging) return;
    const onMove = e => {
      setPlusPos({
        left: e.clientX - dragOffset.current.x,
        top: e.clientY - dragOffset.current.y,
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  // Poll for .ProseMirror ref
  useEffect(() => {
    if (!editor) return;
    const interval = setInterval(() => {
      if (!editorRef.current) {
        const pm = document.querySelector('.ProseMirror');
        if (pm) editorRef.current = pm;
      }
    }, 200);
    return () => clearInterval(interval);
  }, [editor]);

  // Track cursor position for plus button
  useEffect(() => {
    if (!editor) return;
    let timeoutId;
    const update = () => {
      if (menuSource === 'plus') return;
      try {
        if (!editor.view || !editor.view.docView) return;
        const { from } = editor.state.selection;
        const coordsAtCursor = editor.view.coordsAtPos(from);
        const editorBox = editor.view.dom.getBoundingClientRect();
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setCoords({
            top: coordsAtCursor.top + window.scrollY,
            left: editorBox.left + window.scrollX - 36
          });
        }, 50);
      } catch (e) {}
    };
    editor.on("selectionUpdate", update);
    update();
    return () => {
      editor.off("selectionUpdate", update);
      clearTimeout(timeoutId);
    };
  }, [editor, menuSource]);

  // Open menu on /
  useEffect(() => {
    if (!editor) return;
    const onKeyDown = e => {
      const { from } = editor.state.selection;
      const isStartOfLine = editor.state.selection.$from.parentOffset === 0;
      if (e.key === "/" && isStartOfLine) {
        e.preventDefault();
        setMenuSource('editor');
        const lineStartPos = from - editor.state.selection.$from.parentOffset;
        const coordsAtLineStart = editor.view.coordsAtPos(lineStartPos);
        setCoords({ top: coordsAtLineStart.top + window.scrollY, left: coordsAtLineStart.left + window.scrollX });
        setShow(true);
        setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
      }
    };
    editor.on("keydown", onKeyDown);
    return () => editor.off("keydown", onKeyDown);
  }, [editor]);

  // Close on Escape
  useEffect(() => {
    if (!show) return;
    const onKeyDown = e => {
      if (e.key === "Escape") {
        setShow(false);
        setSearch("");
        setSelected(0);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [show]);

  // Close on click outside
  useEffect(() => {
    if (!show) return;
    const onMouseDown = e => {
      if (menuRef.current && !menuRef.current.contains(e.target) && plusRef.current && !plusRef.current.contains(e.target)) {
        setShow(false);
        setSearch("");
        setSelected(0);
      }
    };
    window.addEventListener("mousedown", onMouseDown, true);
    return () => window.removeEventListener("mousedown", onMouseDown, true);
  }, [show]);

  const onMouseDown = e => {
    setDragging(true);
    dragOffset.current = {
      x: e.clientX - plusPos.left,
      y: e.clientY - plusPos.top,
    };
    e.preventDefault();
  };

  return {
    plusPos, setPlusPos,
    dragging, setDragging,
    dragOffset,
    show, setShow,
    coords, setCoords,
    search, setSearch,
    selected, setSelected,
    showTablePicker, setShowTablePicker,
    scrollY, setScrollY,
    menuSource, setMenuSource,
    plusRef, menuRef, inputRef, editorRef,
    onMouseDown,
  };
}
